import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = ['#4F8EF7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];
const PAGE_SIZE = 100;
const MAX_REPORT_ROWS = 2000;

const toAmount = (value) => Number.parseFloat(value || 0) || 0;
const getMonth = (tx) => (tx.date || '').substring(0, 7);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 12, padding: '12px 16px', boxShadow: 'var(--shadow-card)' }}>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ fontSize: 13, fontWeight: 600, color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
      ))}
    </div>
  );
};

export default function Reports() {
  const [tab, setTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLimited, setIsLimited] = useState(false);
  const [period, setPeriod] = useState('6');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setIsLimited(false);

    try {
      const loaded = [];
      let page = 1;
      let lastPage = 1;

      do {
        const res = await api.get('/transactions', { params: { page, per_page: PAGE_SIZE } });
        const rows = res.data.data || [];
        loaded.push(...rows);
        lastPage = res.data.last_page || page;
        page += 1;
      } while (page <= lastPage && loaded.length < MAX_REPORT_ROWS);

      setTransactions(loaded.slice(0, MAX_REPORT_ROWS));
      setIsLimited(page <= lastPage || loaded.length > MAX_REPORT_ROWS);
    } catch {
      setError('Nao foi possivel carregar os relatorios. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reportData = useMemo(() => {
    const monthlyData = transactions.reduce((acc, tx) => {
      const month = getMonth(tx);
      if (!month) return acc;
      if (!acc[month]) acc[month] = { month, income: 0, expense: 0 };
      if (tx.type === 'income') acc[month].income += toAmount(tx.amount);
      if (tx.type === 'expense') acc[month].expense += toAmount(tx.amount);
      return acc;
    }, {});

    const months = Object.keys(monthlyData).sort((a, b) => a.localeCompare(b)).slice(-Number.parseInt(period, 10));
    const monthSet = new Set(months);
    const scopedTransactions = transactions.filter((tx) => monthSet.has(getMonth(tx)));
    const chartData = months.map((month) => {
      const item = monthlyData[month];
      return { ...item, net: item.income - item.expense };
    });

    const categoryData = scopedTransactions.filter((tx) => tx.type === 'expense').reduce((acc, tx) => {
      const name = tx.category?.name || 'Outros';
      if (!acc[name]) acc[name] = { name, value: 0 };
      acc[name].value += toAmount(tx.amount);
      return acc;
    }, {});

    const totalIncome = scopedTransactions
      .filter((tx) => tx.type === 'income')
      .reduce((sum, tx) => sum + toAmount(tx.amount), 0);

    const totalExpense = scopedTransactions
      .filter((tx) => tx.type === 'expense')
      .reduce((sum, tx) => sum + toAmount(tx.amount), 0);

    return {
      catChart: Object.values(categoryData).sort((a, b) => b.value - a.value),
      chartData,
      scopedTransactions,
      totalExpense,
      totalIncome,
    };
  }, [period, transactions]);

  const { catChart, chartData, scopedTransactions, totalExpense, totalIncome } = reportData;

  const tabs = [
    { id: 'overview', label: 'Visao Geral' },
    { id: 'income-expense', label: 'Receita vs Despesa' },
    { id: 'categories', label: 'Por Categoria' },
    { id: 'trends', label: 'Tendencias' },
  ];

  const hasData = scopedTransactions.length > 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Relatorios</h1>
          <p className="page-subtitle">Analise detalhada das suas financas</p>
        </div>
        <select className="form-input form-select" style={{ width: 140 }} value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="3">3 meses</option>
          <option value="6">6 meses</option>
          <option value="12">12 meses</option>
        </select>
      </div>

      {isLimited && (
        <div className="card mb-md" style={{ padding: 14, borderColor: 'var(--accent-amber)', color: 'var(--accent-amber)' }}>
          Relatorio limitado aos {MAX_REPORT_ROWS} movimentos mais recentes para manter a performance.
        </div>
      )}

      <div className="tab-bar">
        {tabs.map((t) => (
          <button key={t.id} className={`tab-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 400, borderRadius: 16 }} />
      ) : error ? (
        <div className="empty-state card">
          <p className="empty-state-title">{error}</p>
          <button className="btn btn-primary" onClick={fetchData}>Tentar novamente</button>
        </div>
      ) : !hasData ? (
        <div className="empty-state card">
          <p className="empty-state-title">Sem dados no periodo</p>
          <p className="empty-state-text">Registe receitas ou despesas para ver relatorios.</p>
        </div>
      ) : (
        <>
          {tab === 'overview' && (
            <div>
              <div className="kpi-grid mb-lg">
                <div className="kpi-card green"><div className="kpi-label">Total Receitas</div><div className="kpi-value text-income">{formatCurrency(totalIncome)}</div></div>
                <div className="kpi-card red"><div className="kpi-label">Total Despesas</div><div className="kpi-value text-expense">{formatCurrency(totalExpense)}</div></div>
                <div className="kpi-card blue"><div className="kpi-label">Saldo Liquido</div><div className="kpi-value">{formatCurrency(totalIncome - totalExpense)}</div></div>
                <div className="kpi-card purple"><div className="kpi-label">Transacoes</div><div className="kpi-value">{scopedTransactions.length}</div></div>
              </div>
              <div className="card">
                <h3 className="section-title mb-md">Evolucao Mensal</h3>
                <div className="chart-container" style={{ height: 320, width: '100%', minWidth: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} />
                      <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="income" name="Receita" stroke="#10B981" fill="rgba(16,185,129,0.1)" strokeWidth={2} />
                      <Area type="monotone" dataKey="expense" name="Despesa" stroke="#EF4444" fill="rgba(239,68,68,0.1)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {tab === 'income-expense' && (
            <div className="card">
              <h3 className="section-title mb-md">Receita vs Despesa por Mes</h3>
              <div className="chart-container" style={{ height: 360, width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={6}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="income" name="Receita" fill="#10B981" radius={[6, 6, 0, 0]} barSize={28} />
                    <Bar dataKey="expense" name="Despesa" fill="#EF4444" radius={[6, 6, 0, 0]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {tab === 'categories' && (
            <div className="grid-2">
              <div className="card">
                <h3 className="section-title mb-md">Despesas por Categoria</h3>
                {catChart.length === 0 ? (
                  <div className="empty-state"><p className="empty-state-text">Sem despesas neste periodo.</p></div>
                ) : (
                  <div style={{ height: 280, width: '100%', minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={catChart.slice(0, 8)} dataKey="value" nameKey="name" cx="50%" cy="50%"
                          innerRadius={60} outerRadius={100} paddingAngle={3} strokeWidth={0}>
                          {catChart.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <div className="card">
                <h3 className="section-title mb-md">Ranking de Categorias</h3>
                {catChart.length === 0 ? (
                  <div className="empty-state"><p className="empty-state-text">Sem despesas neste periodo.</p></div>
                ) : catChart.slice(0, 10).map((cat, i) => (
                  <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-primary)' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white' }}>
                      {i + 1}
                    </div>
                    <span style={{ flex: 1, fontSize: 14 }}>{cat.name}</span>
                    <span className="text-mono" style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(cat.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'trends' && (
            <div className="card">
              <h3 className="section-title mb-md">Tendencia de Poupanca</h3>
              <div className="chart-container" style={{ height: 320, width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="net" name="Poupanca" stroke="#4F8EF7" strokeWidth={3} dot={{ fill: '#4F8EF7', r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
